import { apiURL } from 'config/baseURL';
import { gatewayFetch } from 'helpers/gateway';
import { readErrorMessage } from 'helpers/apiError';
import { storeLoginSession } from 'helpers/sessionAuth';

export interface AuthResult {
	status: string;
	errorMsg: string;
	errorField: string;
	data: any;
}

/** A failure without its always-null `data`, so each one is declared once. */
type Failure = Pick<AuthResult, 'status' | 'errorMsg' | 'errorField'>;

const failed = (failure: Failure): AuthResult => ({ ...failure, data: null });

const BADLY_FORMATTED: Failure = {
	status: 'user_not_found',
	errorMsg: 'Email address is badly formatted. Please try again.',
	errorField: 'email',
};
const WRONG_PASSWORD: Failure = {
	status: 'wrong_password',
	errorMsg: 'Invalid credentials. Please try again',
	errorField: 'password',
};
const TOO_MANY_REQUESTS: Failure = {
	status: 'too_many_requests',
	errorMsg: 'Too many login errors. Please try again later.',
	errorField: '',
};
const USER_DISABLED: Failure = {
	status: 'user_disabled',
	errorMsg: 'Your account has been temporarily disabled.',
	errorField: '',
};
const UNKNOWN: Failure = { status: 'unknown_error', errorMsg: 'Unknown error', errorField: '' };

/**
 * api-users reports each login failure as `{ message }` plus a status. The
 * messages are the exact strings its `LoginFailureReasonMap` holds, and every
 * one of them matches a Firebase `auth/*` failure. Unknown messages fall back
 * to the HTTP status, so a stray `User not found` 401 has the same generic
 * response as an incorrect password.
 */
const FAILURE_BY_MESSAGE: Record<string, Failure> = {
	'Wrong password': WRONG_PASSWORD,
	'User access disabled': USER_DISABLED,
	'Organization access disabled': USER_DISABLED,
	'Invalid email or username': BADLY_FORMATTED,
	'Invalid User Email': BADLY_FORMATTED,
	'Too many attempts. Please try again later.': TOO_MANY_REQUESTS,
};

/**
 * Fallback for a message this build does not know - a reworded server string, a
 * Joi rejection, or a gateway error page that is not JSON at all. The status
 * still separates "the credentials were refused" (401) from "you are being
 * throttled" (429), which are the two a caller must not confuse: a client told
 * its password is wrong when it is actually throttled is sent into a reset loop
 * that cannot clear a throttle.
 */
const failureForStatus = (status: number): Failure => {
	if (status === 429) {
		return TOO_MANY_REQUESTS;
	}
	if (status === 401) {
		return WRONG_PASSWORD;
	}
	return UNKNOWN;
};

const SUCCESS: AuthResult = { status: 'ok', errorMsg: '', errorField: '', data: null };

/**
 * Attempt to authenticate user to the server.
 *
 * The password goes to api-users `/users/login`, which proves it against the
 * authoritative credential store and answers with a Firebase custom token.
 * The browser redeems that token for a session, so everything downstream -
 * the `idtoken` cache, `gatewayFetch`, the admin-lite iframe - is unchanged.
 *
 * Firebase Auth is deliberately no longer asked to verify a password here. A
 * client-side `signInWithEmailAndPassword` is invisible to the credential
 * store, which is what kept every web admin login outside the Phase 3 flip and
 * outside every metric that watches it.
 *
 * @param {string} email user email address, or a username
 * @param {string} password user password binded to email address
 * @param authentication Firebase auth instance used only to redeem the custom token
 *
 * */
export async function authenticateWithCredentialStore(
	email: string,
	password: string,
	authentication: { signInWithCustomToken: (customToken: string) => Promise<unknown> },
): Promise<AuthResult> {
	if (!email || !password) {
		return {
			status: 'field_not_complete',
			errorMsg: 'One or more fields are missing.',
			errorField: '',
			data: null,
		};
	}

	let response: Response;

	try {
		response = await gatewayFetch(`${apiURL}/users/login`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			// Sent as typed. api-users decides for itself whether this is an
			// address or a username and appends the synthetic domain if it is
			// the latter, so the rule lives in one place rather than having to
			// agree across two regexes - and this is what audit-lite already
			// sends to the same service.
			body: JSON.stringify({ email, password }),
		});
	} catch {
		// Never reached the server. The SDK reported this as
		// `auth/network-request-failed`, which fell through to the same
		// unknown-error answer this gives.
		return failed(UNKNOWN);
	}

	if (!response.ok) {
		// gatewayFetch resolves on a plain-fetch 4xx, so the body is the only
		// place the reason is recorded.
		const message = await readErrorMessage(response);

		return failed(FAILURE_BY_MESSAGE[message] ?? failureForStatus(response.status));
	}

	let customToken = '';
	let finishSessionLogin: (succeeded?: boolean) => void = () => {};
	try {
		const body = await response.json();
		customToken = typeof body?.data?.customToken === 'string' ? body.data.customToken : '';
		// Before the Firebase sign-in, so the first API call after it already uses the session.
		finishSessionLogin = storeLoginSession(customToken ? body.data : null);
	} catch {
		// A 2xx that is not JSON cannot establish the required Firebase session.
	}

	if (!customToken) {
		return failed(UNKNOWN);
	}

	try {
		await authentication.signInWithCustomToken(customToken);
		finishSessionLogin();
		return SUCCESS;
	} catch {
		finishSessionLogin(false);
		// Firebase opens the RTDB/Storage session but no longer gets a second
		// chance to decide whether the password is valid. Report the bridge
		// outage and let the user retry.
		return failed(UNKNOWN);
	}
}

async function authenticateCredentials(email: string, password: string, firebase: any): Promise<AuthResult> {
	return authenticateWithCredentialStore(email, password, firebase.auth());
}

export default authenticateCredentials;
